// QuotationPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import axios from 'axios';
import { useAuthContext } from '../common/ProtectedLayout';
import styled from 'styled-components';


const QuotationPage = () => {
    const auth = useAuthContext();
    const [companyName, setCompanyName] = useState('');
    const [items, setItems] = useState([
        { name: '에어컨 분해세척', spec: '4-Way', quantity: 1, price: 150000 }
    ]);

    // ✅ [New] 추가된 상태 (견적번호, 주소, 종목, 비고)
    const [quotationNumber, setQuotationNumber] = useState('');
    const [address, setAddress] = useState('경기도 성남시 중원구 은행동 563');
    const [jongmok, setJongmok] = useState('전자제품설치 외');
    const [bigo, setBigo] = useState('* 유효기간 : 3일간 유효합니다.\n* 취급품목 : 에어컨 분해청소 , 정기방역 소독 , 코로나 방역 (보건복지부-소독업면허 취득)');

    // 다이얼로그 관련 상태
    const [dialogItems, setDialogItems] = useState([]);
    const [dialogCompanyName, setDialogCompanyName] = useState('');
    const [dialogNewName, setDialogNewName] = useState('');
    const [dialogNewSpec, setDialogNewSpec] = useState('');
    const [dialogNewQuantity, setDialogNewQuantity] = useState('');
    const [dialogNewPrice, setDialogNewPrice] = useState('');

    // ✅ [New] 다이얼로그 추가 필드 상태
    const [dialogAddress, setDialogAddress] = useState('');
    const [dialogJongmok, setDialogJongmok] = useState('');
    const [dialogBigo, setDialogBigo] = useState('');

    const modifyDialogRef = useRef();
    const printRef = useRef();

    // ✅ [Updated] DB 기반 자동 넘버링 로직
    const [isSaved, setIsSaved] = useState(false); // 저장 여부

    // ✅ [Updated] 견적번호 조회 함수 (재사용 가능)
    const fetchQuotationNumber = async () => {
        try {
            const staffId = auth?.loginId || 'guest';
            const res = await axios.get(`/Quotation/NewNumber/${staffId}`);
            if (res.data.success) {
                setQuotationNumber(res.data.quotationNumber);
            }
        } catch (error) {
            console.error('견적번호 생성 오류:', error);
            // 오류 시 fallback
            const today = new Date();
            const yy = String(today.getFullYear()).slice(-2);
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            const dateStr = `${yy}${mm}${dd}`;
            const staffId = auth?.loginId || 'guest';
            setQuotationNumber(`No.${dateStr}-${staffId}01`);
        }
    };

    // ✅ 페이지 진입 시 견적번호 조회
    useEffect(() => {
        fetchQuotationNumber();
    }, [auth?.loginId]);


    // ✅ [Updated] 수정 다이얼로그 열기 - 확인 다이얼로그 추가
    const openModifyDialog = () => {
        // 이미 작성된 내용이 있으면 확인
        if (items.length > 0 || companyName) {
            if (!window.confirm('수정하시면 기존에 작성한 내용은 초기화됩니다. 계속하시겠습니까?')) {
                return;
            }
        }

        setDialogItems(JSON.parse(JSON.stringify(items)));
        setDialogCompanyName(companyName);
        setDialogAddress(address);
        setDialogJongmok(jongmok);
        setDialogBigo(bigo);

        setDialogNewName('');
        setDialogNewSpec('');
        setDialogNewQuantity('');
        setDialogNewPrice('');

        if (modifyDialogRef.current) {
            modifyDialogRef.current.showModal();
        }
    };

    // 다이얼로그 닫기
    const closeModifyDialog = () => {
        if (modifyDialogRef.current) {
            modifyDialogRef.current.close();
        }
    };

    const handleDialogAddItem = (e) => {
        e.preventDefault();
        if (!dialogNewName || !dialogNewQuantity || !dialogNewPrice) {
            alert('품명, 수량, 단가는 필수 입력값입니다.');
            return;
        }
        const newItem = {
            name: dialogNewName,
            spec: dialogNewSpec,
            quantity: Number(dialogNewQuantity),
            price: Number(dialogNewPrice)
        };
        setDialogItems([...dialogItems, newItem]);
        setDialogNewName('');
        setDialogNewSpec('');
        setDialogNewQuantity('');
        setDialogNewPrice('');
    };

    const handleDialogDeleteItem = (index) => {
        const newItems = dialogItems.filter((_, i) => i !== index);
        setDialogItems(newItems);
    };

    // ✅ [Updated] 모달 저장 시 견적번호 생성 + DB 저장 + 상태 적용
    const saveDialogChanges = async () => {
        const staffId = auth?.loginId || 'guest';
        let newQuotationNumber = '';

        try {
            // 1. 견적번호 생성 (API 호출)
            const res = await axios.get(`/Quotation/NewNumber/${staffId}`);
            if (res.data.success) {
                newQuotationNumber = res.data.quotationNumber;
            } else {
                // fallback
                const today = new Date();
                const yy = String(today.getFullYear()).slice(-2);
                const mm = String(today.getMonth() + 1).padStart(2, '0');
                const dd = String(today.getDate()).padStart(2, '0');
                const dateStr = `${yy}${mm}${dd}`;
                newQuotationNumber = `No.${dateStr}-${staffId}01`;
            }
        } catch (error) {
            console.error('견적번호 생성 오류:', error);
            const today = new Date();
            const yy = String(today.getFullYear()).slice(-2);
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            const dateStr = `${yy}${mm}${dd}`;
            newQuotationNumber = `No.${dateStr}-${staffId}01`;
        }

        // 2. 상태 적용 (totalAmount 계산을 위해 먼저)
        const newItems = dialogItems;
        const newTotalSupply = newItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);
        const newTotalTax = newItems.reduce((sum, item) => sum + (item.quantity * item.price * 0.1), 0);
        const newTotalAmount = Math.round(newTotalSupply + newTotalTax);

        // 3. DB 저장
        try {
            const saveRes = await axios.post('/Quotation', {
                quotation_number: newQuotationNumber,
                staff_id: staffId,
                company_name: dialogCompanyName,
                address: dialogAddress,
                jongmok: dialogJongmok,
                bigo: dialogBigo,
                items: newItems,
                total_amount: newTotalAmount
            });

            if (saveRes.data.success) {
                // 성공 시 상태 업데이트
                console.log('✅ 저장 성공, 저장된 번호:', newQuotationNumber);
                setItems(newItems);
                setCompanyName(dialogCompanyName);
                setAddress(dialogAddress);
                setJongmok(dialogJongmok);
                setBigo(dialogBigo);
                setIsSaved(true);

                // 모달 닫기 후 다음 견적번호 조회
                closeModifyDialog();

                // 다음 사용 가능한 견적번호 조회 (카운팅 증가 반영)
                setTimeout(() => {
                    fetchQuotationNumber();
                }, 100);
            } else {
                alert('견적서 저장 실패: ' + saveRes.data.message);
            }
        } catch (error) {
            console.error('견적서 저장 오류:', error);
            if (error.response?.status === 409) {
                alert('이미 저장된 견적번호입니다.');
            } else {
                alert('견적서 저장 중 오류가 발생했습니다.');
            }
        }
    };

    const getCurrentDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const calcSupply = (item) => item.quantity * item.price;
    const calcTax = (item) => calcSupply(item) * 0.1;

    // ✅ [Updated] PDF 다운로드만 (DB 저장 X)
    const handleSaveAsPDF = async () => {
        const input = printRef.current;
        if (!input) return;

        // PDF 생성 및 다운로드
        const canvas = await html2canvas(input);
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210;
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgProps = pdf.getImageProperties(imgData);
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        pdf.save('quotation.pdf');
    };

    const currentDate = getCurrentDate();
    const totalSupply = items.reduce((sum, item) => sum + calcSupply(item), 0);
    const totalTax = items.reduce((sum, item) => sum + calcTax(item), 0);
    const totalAmount = totalSupply + totalTax;

    // 다이얼로그 합계
    const dialogTotalSupply = dialogItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    const dialogTotalTax = dialogItems.reduce((sum, item) => sum + (item.quantity * item.price * 0.1), 0);
    const dialogTotalAmount = dialogTotalSupply + dialogTotalTax;

    // 스타일 정의
    const containerStyle = { width: '800px', margin: '50px auto', backgroundColor: '#fff', padding: '20px', fontFamily: 'sans-serif', position: 'relative' };
    const containerBottomStyle = { width: '800px', padding: '20px', margin: '0px auto', fontFamily: 'sans-serif', position: 'relative', textAlign: 'right' };
    const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '20px', border: '3px solid #000', lineHeight: '2' };
    const thStyle = { border: '1px solid #000', textAlign: 'center', padding: '5px', backgroundColor: '#f9f9f9' };
    const tdStyle = { border: '1px solid #000', padding: '5px', textAlign: 'center' };
    const numberTdStyle = { border: '1px solid #000', textAlign: 'right', padding: '5px' };
    const titleStyle = { textAlign: 'center', fontSize: '50px', fontWeight: 'bold', marginBottom: '10px' };

    return (
        <div className="dashboard-container">
            <div className="content-container">
                <div>
                    <div style={containerStyle} ref={printRef}>
                        <div style={titleStyle}>견 적 서</div>
                        {/* ✅ [Updated] 자동 생성된 번호 표시 */}
                        <div style={{ fontSize: '15px', textAlign: 'right' }}>{quotationNumber}</div>
                        <table style={tableStyle}>
                            <tbody>
                                <tr>
                                    <td style={tdStyle} rowSpan="5">
                                        {currentDate}<br />
                                        {companyName ? `${companyName} 귀하.` : '귀하.'}<br />
                                        아래와 같이 견적합니다.
                                    </td>
                                    <td style={tdStyle} rowSpan="5">공급자</td>
                                    <td style={tdStyle}>등록번호</td>
                                    <td style={tdStyle} colSpan="3">614-86-03192</td>
                                </tr>
                                <tr>
                                    <td style={tdStyle}>상호</td>
                                    <td style={tdStyle}>에어컨알지</td>
                                    <td style={tdStyle}>대표자</td>
                                    <td style={tdStyle}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                            <span>조철호</span>
                                            <img src="/stamp.png" alt="도장" style={{ width: '40px', height: '40px', marginLeft: '-5px' }} />
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={tdStyle}>사업장주소</td>
                                    {/* ✅ [Updated] 주소 상태값 사용 */}
                                    <td style={tdStyle} colSpan="3">{address}</td>
                                </tr>
                                <tr>
                                    <td style={tdStyle}>업태</td>
                                    <td style={tdStyle}>서비스</td>
                                    <td style={tdStyle}>종목</td>
                                    {/* ✅ [Updated] 종목 상태값 사용 */}
                                    <td style={tdStyle}>{jongmok}</td>
                                </tr>
                                <tr>
                                    <td style={tdStyle}>대표번호</td>
                                    <td style={tdStyle}>1688-2637</td>
                                    <td style={tdStyle}>FAX</td>
                                    <td style={tdStyle}>031-745-7771</td>
                                </tr>
                                <tr>
                                    <td style={tdStyle} colSpan="6">
                                        금 액 : 영 원정 (₩ {totalAmount.toLocaleString()}) VAT 포함.
                                    </td>
                                </tr>

                                <tr>
                                    <th style={thStyle}>품 명</th>
                                    <th style={thStyle}>규 격</th>
                                    <th style={thStyle}>수 량</th>
                                    <th style={thStyle}>단 가</th>
                                    <th style={thStyle}>공급가액</th>
                                    <th style={thStyle}>세 액</th>
                                </tr>

                                {items.map((item, idx) => {
                                    const supply = calcSupply(item);
                                    const tax = calcTax(item);
                                    return (
                                        <tr key={idx}>
                                            <td style={tdStyle}>{item.name}</td>
                                            <td style={tdStyle}>{item.spec}</td>
                                            <td style={numberTdStyle}>{item.quantity}</td>
                                            <td style={numberTdStyle}>{item.price.toLocaleString()}</td>
                                            <td style={numberTdStyle}>{supply.toLocaleString()}</td>
                                            <td style={numberTdStyle}>{tax.toLocaleString()}</td>
                                        </tr>
                                    );
                                })}
                                <tr>
                                    <td style={tdStyle} colSpan="4" align="center">합 계</td>
                                    <th style={numberTdStyle}>{totalSupply.toLocaleString()}</th>
                                    <th style={numberTdStyle}>{totalTax.toLocaleString()}</th>
                                </tr>
                                <tr>
                                    {/* ✅ [Updated] 비고 상태값 사용 (줄바꿈 처리) */}
                                    <td style={{ marginTop: '20px', textAlign: 'left', lineHeight: '2', whiteSpace: 'pre-wrap' }} colSpan="6">
                                        {bigo}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div style={containerBottomStyle}>
                        <PdfButton onClick={handleSaveAsPDF}>
                            PDF 저장
                        </PdfButton>
                        <EditButton onClick={openModifyDialog}>
                            수정
                        </EditButton>
                    </div>
                </div>
            </div>

            {/* 수정 다이얼로그 */}
            <dialog ref={modifyDialogRef} style={{ padding: '20px', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '800px', width: '90%' }}>
                <h3 style={{ marginTop: 0 }}>견적 수정</h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>귀하 회사명</label>
                        <input
                            type="text"
                            value={dialogCompanyName}
                            onChange={(e) => setDialogCompanyName(e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>사업장 주소</label>
                        <input
                            type="text"
                            value={dialogAddress}
                            onChange={(e) => setDialogAddress(e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>종목</label>
                        <input
                            type="text"
                            value={dialogJongmok}
                            onChange={(e) => setDialogJongmok(e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                        />
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>비고 (하단 문구)</label>
                        <textarea
                            value={dialogBigo}
                            onChange={(e) => setDialogBigo(e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '80px', boxSizing: 'border-box' }}
                        />
                    </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', lineHeight: '2' }}>
                    <tbody>
                        <tr>
                            <th style={thStyle}>품 명</th>
                            <th style={thStyle}>규 격</th>
                            <th style={thStyle}>수 량</th>
                            <th style={thStyle}>단 가</th>
                            <th style={thStyle}>공급가액</th>
                            <th style={thStyle}>세 액</th>
                            <th style={thStyle}>삭제</th>
                        </tr>
                        {dialogItems.map((item, idx) => {
                            const supply = item.quantity * item.price;
                            const tax = supply * 0.1;
                            return (
                                <tr key={idx}>
                                    <td style={tdStyle}>{item.name}</td>
                                    <td style={tdStyle}>{item.spec}</td>
                                    <td style={numberTdStyle}>{item.quantity}</td>
                                    <td style={numberTdStyle}>{item.price.toLocaleString()}</td>
                                    <td style={numberTdStyle}>{supply.toLocaleString()}</td>
                                    <td style={numberTdStyle}>{tax.toLocaleString()}</td>
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                        <button onClick={() => handleDialogDeleteItem(idx)} style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>삭제</button>
                                    </td>
                                </tr>
                            );
                        })}
                        <tr>
                            <td style={tdStyle} colSpan="4" align="center">합 계</td>
                            <th style={numberTdStyle}>{dialogTotalSupply.toLocaleString()}</th>
                            <th style={numberTdStyle}>{dialogTotalTax.toLocaleString()}</th>
                            <td style={tdStyle}></td>
                        </tr>
                        <tr>
                            <td style={tdStyle} colSpan="7" align="right">
                                总액(공급가액+세액): {dialogTotalAmount.toLocaleString()} 원
                            </td>
                        </tr>
                    </tbody>
                </table>

                <form onSubmit={handleDialogAddItem} style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '10px' }}>새 항목 추가</h4>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="text" value={dialogNewName} onChange={(e) => setDialogNewName(e.target.value)} placeholder="품명" style={{ padding: '8px', flex: 2, border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }} />
                        <input type="text" value={dialogNewSpec} onChange={(e) => setDialogNewSpec(e.target.value)} placeholder="규격" style={{ padding: '8px', flex: 1, border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }} />
                        <StyledNumberInput type="number" value={dialogNewQuantity} onChange={(e) => setDialogNewQuantity(e.target.value)} placeholder="수량" style={{ width: '80px', boxSizing: 'border-box' }} />
                        <StyledPriceInput type="number" value={dialogNewPrice} onChange={(e) => setDialogNewPrice(e.target.value)} placeholder="단가" style={{ width: '100px', boxSizing: 'border-box' }} />
                        <button type="submit" style={{ padding: '8px 15px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>추가</button>
                    </div>
                </form>

                <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button onClick={closeModifyDialog} style={{ padding: '8px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>취소</button>
                    <button onClick={saveDialogChanges} style={{ padding: '8px 20px', backgroundColor: '#006edc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>저장</button>
                </div>
            </dialog>
        </div>
    );
};

export default QuotationPage;

const StyledNumberInput = styled.input`
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        opacity: 1;
        height: 30px;
        width: 20px;
        cursor: pointer;
    }
`;

const StyledPriceInput = styled.input`
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    &[type=number] {
        -moz-appearance: textfield;
    }
`;

const PdfButton = styled.button`
    padding: 10px 20px;
    width: 120px;
    background-color: #006edc;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    margin-right: 10px;
    transition: all 0.2s;

    &:hover {
        background-color: #0056b3;
    }
`;

const EditButton = styled.button`
    padding: 10px 20px;
    width: 120px;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background-color: #5a6268;
    }
`;
